<script lang="ts" setup>
import type { GetBalanceReturnType } from '@wagmi/core/actions';
import type { Address } from 'viem';

import { computed } from '#imports';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useUserStore } from '~/stores/user/useUserStore';
import { showCurrencyIcon, toShortAddress } from '~/utils/helpers';

import type { Web3AccountInfoStatus } from './Web3AccountInfo.types';

type Props = {
    address: Address;
    status?: Web3AccountInfoStatus;
    chainName: string;
    balance: Nullable<GetBalanceReturnType>;
};

const props = defineProps<Props>();

const alertStore = useAlertsStore();

const userStore = useUserStore();

const currencyIcon = computed(() => props.balance ? showCurrencyIcon(props.balance.symbol, userStore.chainId) : undefined);

const copyToClipboard = () => {
    navigator.clipboard.writeText(props.address).then(() => {
        alertStore.add({

            critical: false,
            description: 'Address is copied to the clipboard',
            status: 'type.success',
            title: 'Copied'
        });
    })
        .catch((err) => {
            console.error(
                'Failed to copy text: ',
                err
            );
        });
};

const shortAddress = computed(() => toShortAddress(props.address));

</script>
<template>
  <div v-bind="$attrs"
       class="account-wallet display-flex align-items-center gap-16"
  >
    <div class="account-wallet__icon display-flex align-items-center justify-center">
      <IconMain v-if="currencyIcon"
                :icon="currencyIcon"
                :size="25"
      />
    </div>
    <div class="grow">
      <p class="text-500-default">{{ chainName }}</p>
      <span class="text-500-default">{{ shortAddress }}</span>
    </div>

    <span v-if="status"
          :class="`account-status--${status}`"
    >{{ status }}</span>

    <div v-else
         type="button"
         role="button"
         aria-label="copy"
         class="account-wallet__copy cursor-pointer"
         @click="copyToClipboard"
    >
      <IconMain icon="copy"
                :size="25"
      />
    </div>
  </div>
</template>

<style lang="scss">
%spacing-x {
  padding-right: $spacing-600;
  padding-left: $spacing-600;

  @include tablet-768 {
    padding-right: $spacing-400;
    padding-left: $spacing-400;
  }
}

.account {
  align-self: stretch;

  &-status {
    &--connected {
      color: var(--bg-brand-mojito-primary, #92FE75);
    }
  }

  &-wallet {
    &__icon {
      width: 52px;
      height: 52px;
      border-radius: 5px;
      background-color: var(--bg-tertary);
    }

    &__copy {
      margin-left: auto
    }
  }
}
</style>
